import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import progressBackgroundUrl from 'data-url:../static/images/form_background_waves_double.svg';
import { modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet } from './shared-styles.js';

// macOS provide filenames in decomposed Unicode (NFD), which can render
// Turkish, Spanish, Korean combining marks with visual offsets in some fonts. Normalize to NFC.
const normalizeFileName = (name) => (typeof name === 'string' ? name.normalize('NFC') : '');

export default class ProgressDivElement extends LitElement {
    static properties = {
        fileName: { attribute: false },
        progressValue: { attribute: false },
    };

    static styles = [modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet, css`
        :host {
            display: block;
        }

        /* Without this, setting the \`hidden\` attribute/property has no visible effect:
           the UA stylesheet's [hidden] { display: none; } is a lower-priority origin
           than this component's own :host { display: block; } rule above, so origin
           order (author beats user-agent, regardless of specificity) lets :host win
           and the element stays visible even while hidden. */
        :host([hidden]) {
            display: none;
        }

        .progress-div {
            animation: fadeIn 0.5s ease-out;
            background-color: var(--color-form-bg);
            /* background-image is set via styleMap() in render(), not here */
            background-position: bottom;
            border-radius: 20px;
            box-shadow: var(--shadow-input);
            font-family: var(--font-body);
            padding-block-end: 30px;
            padding-block-start: 150px;
            position: relative;
        }

        .progress-div__animation {
            height: 300px;
            left: 35%;
            position: absolute;
            top: -35%;
            width: 300px;
        }

        .progress-div__file-name {
            overflow: hidden;
            text-align: center;
            text-overflow: ellipsis;
            text-wrap: nowrap;
        }

        .progress-div__progress-bar {
            background-color: var(--color-input-bg);
            border-radius: 10px;
            box-shadow: var(--shadow-input); /* for Firefox */
            color: var(--color-input-primary);
            display: block;
            height: 20px;
            overflow: hidden;
            width: 100%;
        }

        .progress-div__progress-bar::-webkit-progress-bar {
            background-color: var(--color-input-bg);
            border-radius: 10px;
            box-shadow: var(--shadow-input); /* for Chrome */
        }

        .progress-div__progress-bar::-webkit-progress-value {
            background-color: var(--color-input-primary);
            border-radius: 10px;
        }

        .progress-div__progress-bar::-moz-progress-bar {
            background-color: var(--color-input-primary);
            border-radius: 10px;
        }

        /* xs */
        @media screen and (min-width: 320px) {
            .progress-div__animation {
                left: 0%;
            }
        }

        /* sm */
        @media screen and (min-width: 384px) {
            .progress-div__animation {
                left: 10%;
            }
        }

        /* md */
        @media screen and (min-width: 448px) {
            .progress-div__animation {
                left: 15%;
            }

            .progress-div__file-name {
                font-size: 1rem;
            }
        }

        /* xl */
        @media screen and (min-width: 576px) {
            .progress-div__animation {
                left: 25%;
            }

            .progress-div__file-name {
                font-size: 1.25rem;
            }
        }

        /* 3xl */
        @media screen and (min-width: 768px) {
            .progress-div__animation {
                left: 30%;
            }

            .progress-div__file-name {
                font-size: 1.5rem;
            }
        }

        /* 5xl */
        @media screen and (min-width: 1024px) {
            .progress-div__animation {
                left: 35%;
            }

            .progress-div__progress-bar {
                max-width: 768px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .progress-div {
                animation: none;
            }
        }
    `];

    constructor() {
        super();
        this.fileName = 'File name here';
        this.progressValue = 0;
    }

    // Overrides the native reflected `hidden` accessor (inherited from HTMLElement.prototype,
    // not declared via static properties) so that becoming visible always loads/plays the
    // animation — the component owns this pairing itself, rather than requiring every caller
    // that toggles `.hidden` to remember to also call `loadAnimation()`.
    get hidden() {
        return super.hidden;
    }

    set hidden(value) {
        super.hidden = value;

        if (!value) {
            this.loadAnimation();
        }
    }

    #currentAnimation = null;
    #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    #handleReducedMotionChange = (event) => {
        if (!this.#currentAnimation) return;

        if (event.matches) {
            this.#currentAnimation.pause();
        } else {
            this.#currentAnimation.play();
        }
    };

    connectedCallback() {
        super.connectedCallback();
        this.#reducedMotionQuery.addEventListener('change', this.#handleReducedMotionChange);
    }

    disconnectedCallback() {
        this.#reducedMotionQuery.removeEventListener('change', this.#handleReducedMotionChange);
        this.#currentAnimation?.destroy();
        super.disconnectedCallback();
    }

    // Loads (or reloads) the Lottie plane animation into .progress-div__animation. Mirrors
    // static/script/index.js's progressAnimationEl handling — must be called only once this
    // component is visible and its layout has settled, since Lottie measures the container's
    // size at loadAnimation() time. Called automatically from the `hidden` setter above.
    async loadAnimation() {
        const animationContainer = this.renderRoot.querySelector('.progress-div__animation');
        if (!animationContainer) return;

        const [lottieModule, animationModule] = await Promise.all([
            import('lottie-web/build/player/esm/lottie_svg.min.js'),
            import('../static/images/animation_plane.json'),
        ]);

        // Parcel dynamic imports don't apply ESM interop to JSON/CommonJS assets:
        // the JSON module resolves to the parsed object itself, with no `.default`.
        const lottie = lottieModule.default ?? lottieModule;
        const animationData = animationModule.default ?? animationModule;

        this.#currentAnimation?.destroy();

        this.#currentAnimation = lottie.loadAnimation({
            container: animationContainer,
            renderer: 'svg',
            loop: true,
            autoplay: false,
            animationData,
        });

        if (this.#reducedMotionQuery.matches) {
            this.#currentAnimation.pause();
        } else {
            this.#currentAnimation.play();
        }
    }

    // Public: called by an ancestor (e.g. <upload-container>) once this component
    // is shown, to move focus onto the progress bar for screen readers — mirrors
    // static/script/index.js's submitButton handler calling progressBar.focus()
    // right after revealing #div-progress.
    focusProgressBar() {
        this.renderRoot.getElementById('progress-upload').focus();
    }

    render() {
        // background-image for .progress-div, kept out of static styles css since Parcel can't resolve data-url: inside it
        return html`
            <div id="div-progress" class="container-padding progress-div" role="status" aria-live="polite" style=${styleMap({ backgroundImage: `url(${progressBackgroundUrl})` })}>
                <div class="progress-div__animation mi-auto" aria-hidden="true"></div>
                <p class="progress-div__file-name">${normalizeFileName(this.fileName)}</p>
                <label class="visually-hidden" for="progress-upload">Upload progress: </label>
                <progress id="progress-upload" class="progress-div__progress-bar mt-1rem mi-auto" value="${this.progressValue}" max="100" tabindex="-1"></progress>
            </div>
        `;
    }
}

customElements.define('tempup-progress-div', ProgressDivElement);
